import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Slider } from '@/components/ui/Slider';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { useAutobuy, useTriggerAutouse, useUpdateAutobuy } from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/cn';
import type { AutobuySlot } from '@/types';

const SLOT_COUNT = 4;
const AUTOUSE_INTERVAL_MS = 2_000;

export function AutobuyTab() {
  const userId = useAuthStore((s) => s.userId);
  const openModal = useUIStore((s) => s.openModal);
  const { data, isLoading } = useAutobuy(userId);
  const update = useUpdateAutobuy(userId ?? 0);
  const trigger = useTriggerAutouse(userId ?? 0);

  const [enabled, setEnabled] = useState(false);
  const [goldLimit, setGoldLimit] = useState(0);
  const [slots, setSlots] = useState<AutobuySlot[]>([]);
  const [energyThreshold, setEnergyThreshold] = useState(0);
  const [healthThreshold, setHealthThreshold] = useState(0);
  const [autouseCoffee, setAutouseCoffee] = useState(false);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled ?? false);
    setGoldLimit(data.gold_limit ?? 0);
    const baseSlots: AutobuySlot[] = Array.from({ length: SLOT_COUNT }, (_, i) =>
      data.slots?.[i] ?? { item_id: null, quantity: 5 },
    );
    setSlots(baseSlots);
    setEnergyThreshold(data.autouse_energy_threshold ?? 0);
    setHealthThreshold(data.autouse_health_threshold ?? 0);
    setAutouseCoffee(data.autouse_coffee ?? false);
  }, [data]);

  // Autouse polling: only when at least one threshold/flag is active.
  const autouseActive = energyThreshold > 0 || healthThreshold > 0 || autouseCoffee;
  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!autouseActive) return;
    intervalRef.current = window.setInterval(() => {
      trigger.mutate();
    }, AUTOUSE_INTERVAL_MS);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autouseActive]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !data) {
    return <div className="text-white/40 text-sm">Загружаем…</div>;
  }

  const locked = !data.autobuy_unlocked;
  const commit = (patch: Record<string, unknown>) => update.mutate(patch);

  return (
    <div className={cn('space-y-3 relative', locked && 'opacity-60')}>
      {locked && (
        <div className="absolute inset-0 z-10 bg-bg/60 backdrop-blur-[2px] rounded-2xl flex flex-col items-center justify-center gap-2 text-center p-6 pointer-events-none">
          <div className="text-2xl">🔒</div>
          <div className="text-white font-medium">Автопокупка заблокирована</div>
          <div className="text-xs text-white/60">
            Открой умение «Autobuy» в дереве навыков
          </div>
        </div>
      )}

      <Card className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
            Автозакупка
          </h3>
          <Toggle
            checked={enabled}
            onChange={(v) => {
              setEnabled(v);
              commit({ enabled: v });
            }}
            disabled={locked}
          />
        </div>
        <p className="text-xs text-white/50 leading-snug">
          Автоматически покупать предметы при входе в город
        </p>
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
          Слоты автозакупки
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {slots.map((slot, i) => (
            <button
              key={i}
              className="p-3 rounded-xl bg-bg-elevated border border-white/5 text-left active:scale-[0.98] disabled:opacity-50"
              disabled={locked}
              onClick={() =>
                openModal('autobuy-item', {
                  slotIndex: i,
                  currentItemId: slot.item_id,
                  onSelect: (itemId: string | null, maxQty: number) => {
                    const next = [...slots];
                    next[i] = { item_id: itemId, quantity: maxQty };
                    setSlots(next);
                    commit({ slots: next });
                  },
                })
              }
            >
              <div className="text-[11px] text-white/50">Слот {i + 1}</div>
              <div className="text-sm text-white font-medium mt-0.5">
                {slot.item_id ?? 'Пусто'}
              </div>
              {slot.item_id && (
                <div className="text-[10px] text-white/40">до {slot.quantity} шт</div>
              )}
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4 space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
          Лимит золота
        </h3>
        <p className="text-xs text-white/50 leading-snug">
          Максимальная сумма для автозакупки (0 = без лимита)
        </p>
        <Slider
          value={goldLimit}
          min={0}
          max={100000}
          step={500}
          onChange={setGoldLimit}
          onCommit={(v) => commit({ gold_limit: v })}
          suffix="🪙"
          disabled={locked || !enabled}
        />
      </Card>

      <Card className="p-4 space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
          Фиалы энергии
        </h3>
        <p className="text-xs text-white/50 leading-snug">
          Использовать фиал, если энергия ниже указанного %
        </p>
        <Toggle
          checked={energyThreshold > 0}
          onChange={(v) => {
            const value = v ? 50 : 0;
            setEnergyThreshold(value);
            commit({ autouse_energy_threshold: value });
          }}
          label="Включить"
        />
        {energyThreshold > 0 && (
          <Slider
            value={energyThreshold}
            min={0}
            max={100}
            onChange={setEnergyThreshold}
            onCommit={(v) => commit({ autouse_energy_threshold: v })}
            suffix="%"
          />
        )}
      </Card>

      <Card className="p-4 space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
          Зелья здоровья
        </h3>
        <p className="text-xs text-white/50 leading-snug">
          Лечить вайфу, если HP ниже указанного % (не действует на павших)
        </p>
        <Toggle
          checked={healthThreshold > 0}
          onChange={(v) => {
            const value = v ? 30 : 0;
            setHealthThreshold(value);
            commit({ autouse_health_threshold: value });
          }}
          label="Включить"
        />
        {healthThreshold > 0 && (
          <Slider
            value={healthThreshold}
            min={0}
            max={100}
            onChange={setHealthThreshold}
            onCommit={(v) => commit({ autouse_health_threshold: v })}
            suffix="%"
          />
        )}
      </Card>

      <Card className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
            Кофе (воскрешение)
          </h3>
          <Toggle
            checked={autouseCoffee}
            onChange={(v) => {
              setAutouseCoffee(v);
              commit({ autouse_coffee: v });
            }}
          />
        </div>
        <p className="text-xs text-white/50 leading-snug">
          Автоматически воскрешать павших вайфу
        </p>
      </Card>

      <Button
        fullWidth
        variant="secondary"
        onClick={() => trigger.mutate()}
        disabled={trigger.isPending}
      >
        {trigger.isPending ? '…' : 'Применить сейчас'}
      </Button>
    </div>
  );
}
